// FormField primitive — a11y-correct label + input wiring.
// Generates stable ids via useId(); hint + error have derived ids for
// aria-describedby. error supersedes hint visually but both ids are always
// included so screen-readers see the hint text regardless of error state.
//
// Three thin wrappers cover ~85% of use-sites:
//   FormFieldInput, FormFieldTextarea, FormFieldSelect
//
// Usage (render-prop):
//   <FormField label="Email" name="email" error={errors.email}>
//     {(wired) => <input {...wired} className="..." />}
//   </FormField>
//
// Usage (wrapper):
//   <FormFieldInput label="Email" name="email" error={errors.email}
//     value={email} onChange={setEmail} />
//
// counter prop — shows live character count; aria-live="polite" announces
// changes without interrupting the user mid-input.
//   <FormFieldTextarea label="Bio" name="bio" counter={{ current: value.length, max: 200 }} />
import {
  useId,
  type ReactNode,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  type SelectHTMLAttributes,
} from "react";
import { cn } from "@/lib/utils/cn";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface WiredInputProps {
  id: string;
  name: string;
  "aria-invalid": true | undefined;
  "aria-describedby": string | undefined;
  "aria-required": true | undefined;
  required: boolean | undefined;
}

interface CounterProp {
  current: number;
  max: number;
}

interface FormFieldProps {
  label: string;
  name: string;
  hint?: string | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
  /** Render label as sr-only (visually hidden but accessible). */
  srOnly?: boolean | undefined;
  /** Live character counter rendered below/beside the input. */
  counter?: CounterProp | undefined;
  children: (wired: WiredInputProps) => ReactNode;
}

// ── Core primitive ─────────────────────────────────────────────────────────────

interface AriaDescribedByArgs {
  hint: string | undefined;
  error: string | undefined;
  counter: CounterProp | undefined;
  hintId: string;
  errorId: string;
  counterId: string;
}

function buildAriaDescribedBy(args: AriaDescribedByArgs): string | undefined {
  const parts: string[] = [];
  if (args.hint) parts.push(args.hintId);
  if (args.error) parts.push(args.errorId);
  if (args.counter) parts.push(args.counterId);
  return parts.length > 0 ? parts.join(" ") : undefined;
}

interface WiredPropsArgs {
  inputId: string;
  name: string;
  error: string | undefined;
  required: boolean | undefined;
  ariaDescribedBy: string | undefined;
}

function buildWiredProps({
  inputId,
  name,
  error,
  required,
  ariaDescribedBy,
}: WiredPropsArgs): WiredInputProps {
  return {
    id: inputId,
    name,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": ariaDescribedBy,
    "aria-required": required ? true : undefined,
    required,
  };
}

export function FormField({
  label,
  name,
  hint,
  error,
  required,
  srOnly = false,
  counter,
  children,
}: FormFieldProps): React.ReactElement {
  const baseId = useId();
  const inputId = `${baseId}-input`;
  const hintId = `${baseId}-hint`;
  const errorId = `${baseId}-error`;
  const counterId = `${baseId}-counter`;
  const ariaDescribedBy = buildAriaDescribedBy({
    hint,
    error,
    counter,
    hintId,
    errorId,
    counterId,
  });
  const wired = buildWiredProps({
    inputId,
    name,
    error,
    required,
    ariaDescribedBy,
  });

  return (
    <div className="space-y-1">
      <label
        htmlFor={inputId}
        className={cn(
          "text-sm font-medium leading-none",
          srOnly && "sr-only pointer-events-none",
        )}
      >
        {label}
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-destructive">
            *
          </span>
        )}
      </label>
      {children(wired)}
      {hint && (
        // Render hint in DOM even when error is present so aria-describedby
        // can reference the hint id and screen-readers still see hint text.
        // sr-only when error is also shown keeps the visual hierarchy clean.
        <p
          id={hintId}
          className={cn("text-xs text-muted-foreground", error && "sr-only")}
        >
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      {counter && (
        // aria-live="polite" announces count changes to screen-readers
        // without interrupting ongoing speech.
        <div
          id={counterId}
          aria-live="polite"
          className={cn(
            "text-xs text-muted-foreground text-right",
            counter.current > counter.max && "text-destructive",
          )}
        >
          {counter.current}/{counter.max}
        </div>
      )}
    </div>
  );
}

// ── FormFieldInput ─────────────────────────────────────────────────────────────

type FormFieldInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "name" | "id"
> & {
  label: string;
  name: string;
  hint?: string | undefined;
  error?: string | undefined;
  srOnly?: boolean | undefined;
};

export function FormFieldInput({
  label,
  name,
  hint,
  error,
  required,
  srOnly,
  className,
  ...inputProps
}: FormFieldInputProps): React.ReactElement {
  return (
    <FormField
      label={label}
      name={name}
      hint={hint}
      error={error}
      required={required}
      srOnly={srOnly}
    >
      {(wired) => (
        <input
          {...wired}
          {...inputProps}
          className={cn(
            "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-destructive focus-visible:ring-destructive",
            className,
          )}
        />
      )}
    </FormField>
  );
}

// ── FormFieldTextarea ──────────────────────────────────────────────────────────

type FormFieldTextareaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "name" | "id"
> & {
  label: string;
  name: string;
  hint?: string | undefined;
  error?: string | undefined;
  srOnly?: boolean | undefined;
  counter?: CounterProp | undefined;
};

export function FormFieldTextarea({
  label,
  name,
  hint,
  error,
  required,
  srOnly,
  counter,
  className,
  ...textareaProps
}: FormFieldTextareaProps): React.ReactElement {
  return (
    <FormField
      label={label}
      name={name}
      hint={hint}
      error={error}
      required={required}
      srOnly={srOnly}
      counter={counter}
    >
      {(wired) => (
        <textarea
          {...wired}
          {...textareaProps}
          className={cn(
            "flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-destructive focus-visible:ring-destructive",
            className,
          )}
        />
      )}
    </FormField>
  );
}

// ── FormFieldSelect ────────────────────────────────────────────────────────────

interface SelectOption {
  value: string;
  label: string;
}

type FormFieldSelectProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "name" | "id"
> & {
  label: string;
  name: string;
  hint?: string | undefined;
  error?: string | undefined;
  srOnly?: boolean | undefined;
  options?: readonly SelectOption[] | undefined;
};

export function FormFieldSelect({
  label,
  name,
  hint,
  error,
  required,
  srOnly,
  options,
  className,
  children,
  ...selectProps
}: FormFieldSelectProps): React.ReactElement {
  return (
    <FormField
      label={label}
      name={name}
      hint={hint}
      error={error}
      required={required}
      srOnly={srOnly}
    >
      {(wired) => (
        <select
          {...wired}
          {...selectProps}
          className={cn(
            "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-destructive focus-visible:ring-destructive",
            className,
          )}
        >
          {options
            ? options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))
            : children}
        </select>
      )}
    </FormField>
  );
}
